type DateInput = string | number | Date | null | undefined

function toDate(v: DateInput): Date | null {
  if (v == null || v === '') return null
  const d = v instanceof Date ? v : new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

const FALLBACK = '—'

// De datum overal met twee cijfers voor dag en maand en vier voor het jaar (05-03-2026,
// 05.03.2026, 05/03/2026) — spec 2026-09-26-taalcodes-taal-regio §3.4. `dateStyle: 'short'`
// gaf in het Nederlands `5-3-2026` en in het Duits een jaar van twee cijfers.
const DATE_PARTS = [
  'dateStyle',
  'timeStyle',
  'weekday',
  'era',
  'year',
  'month',
  'day',
  'hour',
  'minute',
  'second',
  'dayPeriod',
  'fractionalSecondDigits',
] as const
export const DATE_2DIGIT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
}
export const DATETIME_2DIGIT: Intl.DateTimeFormatOptions = {
  ...DATE_2DIGIT,
  hour: '2-digit',
  minute: '2-digit',
}
export const TIME_2DIGIT: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
}

/**
 * Of de aanroeper zelf zegt WAT er getoond wordt. Dan mengen we de standaardvorm er niet
 * bij: `dateStyle` naast `day`/`month` laat Intl een TypeError gooien.
 */
function choosesParts(o?: Intl.DateTimeFormatOptions): boolean {
  return !!o && DATE_PARTS.some((k) => o[k] !== undefined)
}

/** Locale-aware date formatter. Pass the locale from `useLocale()`. */
export function formatDate(
  value: DateInput,
  locale: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = toDate(value)
  if (!d) return FALLBACK
  const opts = choosesParts(options) ? options : { ...DATE_2DIGIT, ...options }
  return new Intl.DateTimeFormat(locale, opts).format(d)
}

/** Date + time formatter: dezelfde datumvorm, plus uren en minuten. */
export function formatDateTime(
  value: DateInput,
  locale: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const opts: Intl.DateTimeFormatOptions | undefined = choosesParts(options)
    ? options
    : { ...DATETIME_2DIGIT, ...options }
  return formatDate(value, locale, opts)
}

/**
 * Locale-aware currency formatter. `currency` must be an ISO 4217 code
 * (e.g. 'EUR', 'USD'). Falls back to a plain number string when the
 * currency code is invalid.
 */
export function formatCurrency(
  amount: number | string | null | undefined,
  locale: string,
  currency: string,
): string {
  if (amount == null || amount === '') return FALLBACK
  const num = typeof amount === 'string' ? Number(amount) : amount
  if (!Number.isFinite(num)) return String(amount)
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(num)
  } catch {
    return `${currency} ${num.toLocaleString(locale)}`
  }
}

/** Locale-aware number formatter. */
export function formatNumber(
  value: number | string | null | undefined,
  locale: string,
  options?: Intl.NumberFormatOptions,
): string {
  if (value == null || value === '') return FALLBACK
  const num = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(num)) return String(value)
  return new Intl.NumberFormat(locale, options).format(num)
}

/** Percentage formatter. Pass a fraction (0.21 → "21%"). */
export function formatPercent(
  value: number | string | null | undefined,
  locale: string,
  options?: Intl.NumberFormatOptions,
): string {
  return formatNumber(value, locale, { style: 'percent', ...options })
}

/**
 * Een tarief dat als percentage is opgeslagen (`"21.000"` = 21%), zoals een btw-tarief: zonder
 * overbodige nullen en in de vorm van de taal (21%, 9,5%, 7 %). Een kale `${r.percentage}%`
 * gaf "21.000%" op een factuurregel.
 */
export function formatRatePercent(percentage: number | string, locale: string): string {
  return formatPercent(Number(percentage) / 100, locale, { maximumFractionDigits: 3 })
}

/**
 * Parses a user-typed decimal string accepting either `.` or `,` as the
 * decimal separator. Returns NaN for unparseable input. Strips spaces and
 * thousands separators so "1.234,56" and "1,234.56" both work, with the
 * last separator treated as the decimal mark.
 */
export function parseDecimal(value: string | number | null | undefined): number {
  if (value == null || value === '') return Number.NaN
  if (typeof value === 'number') return value
  const trimmed = value.replace(/\s/g, '')
  const lastComma = trimmed.lastIndexOf(',')
  const lastDot = trimmed.lastIndexOf('.')
  let normalized: string
  if (lastComma === -1 && lastDot === -1) {
    normalized = trimmed
  } else if (lastComma > lastDot) {
    normalized = trimmed.replace(/\./g, '').replace(',', '.')
  } else {
    normalized = trimmed.replace(/,/g, '')
  }
  return Number(normalized)
}

/** parseDecimal but coerces NaN to 0 — handy for live UI math. */
export function parseDecimalOr0(value: string | number | null | undefined): number {
  const n = parseDecimal(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * '2500.0000' → '2500'. Een aantal is een TELLING, maar een `numeric(18,4)`-kolom
 * geeft er altijd vier decimalen bij terug.
 *
 * Alleen bij het INLEZEN toepassen, nooit per toetsaanslag: wie '2500.5' intypt
 * passeert onderweg '2500.' en zou dat teken meteen weer kwijtraken. Voor een
 * alleen-lezen kolom is `formatNumber` beter — die kent ook de notatie van de kijker;
 * deze is voor een INVOERVELD, dat een ruwe bewerkbare string moet houden.
 *
 * Een PRIJS gaat hier bewust niet doorheen: 0,4760 per stuk is in deze branche een
 * echte precisie, geen opvulling.
 */
export function trimQuantity(v: string | null): string | null {
  if (v == null || v === '' || !v.includes('.')) return v
  return v.replace(/0+$/, '').replace(/\.$/, '')
}
